import React from 'react';
import { ShieldCheck, FileText, Globe, Factory, CheckCircle2, AlertTriangle, BookOpen, ArrowRight, Beaker, ShoppingBag, Tag } from 'lucide-react';

const ResourcesPage: React.FC = () => {
  return (
    <div className="bg-white min-h-screen pb-24">
      {/* Header */}
      <section className="bg-black py-24 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-orange-600/20 rounded-full blur-[100px] -mr-20 -mt-20 pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
            <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-gray-900 border border-gray-800 mb-6">
                <span className="text-orange-500 text-xs font-bold uppercase tracking-widest">
                    Knowledge Base
                </span>
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 tracking-tight">
                Manufacturing <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-orange-700">Hub</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                Everything you need to know about manufacturing supplements in India for domestic sale or global export.
            </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 relative z-20 space-y-24">
        
        {/* Quality & Compliance Section */}
        <section id="gmp" className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-gray-100 scroll-mt-28">
            <div className="flex items-center mb-8">
                <div className="w-14 h-14 bg-green-50 rounded-2xl flex items-center justify-center mr-5 border border-green-100">
                    <Factory className="w-7 h-7 text-green-600" />
                </div>
                <div>
                    <h2 className="text-3xl font-bold text-gray-900">Quality & Compliance</h2>
                    <p className="text-gray-500">FSSAI (India) & FDA (Global)</p>
                </div>
            </div>

            <div className="prose prose-lg text-gray-600">
                <p>
                    Whether you are selling on Amazon India or exporting to Dubai, compliance is non-negotiable. Our facilities handle the regulatory heavy lifting for both markets.
                </p>
                
                <div className="grid md:grid-cols-2 gap-6 mt-6">
                    <div className="bg-gray-50 p-6 rounded-xl">
                        <h4 className="font-bold text-gray-900 mb-2">For India (FSSAI):</h4>
                        <ul className="text-sm space-y-2">
                            <li>• Valid FSSAI Manufacturing License</li>
                            <li>• Schedule M / GMP Compliance</li>
                            <li>• Labeling Regulations (Red/Green Dot)</li>
                            <li>• NABL Lab Testing Reports</li>
                        </ul>
                    </div>
                    <div className="bg-gray-50 p-6 rounded-xl">
                        <h4 className="font-bold text-gray-900 mb-2">For Export (Global):</h4>
                        <ul className="text-sm space-y-2">
                            <li>• US-FDA Registered Facility</li>
                            <li>• WHO-GMP Certified</li>
                            <li>• ISO 22000:2018</li>
                            <li>• Documentation (COA, COO)</li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>

        {/* Pricing & Economics Section */}
        <section id="pricing" className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-gray-100 scroll-mt-28">
            <div className="flex items-center mb-8">
                <div className="w-14 h-14 bg-purple-50 rounded-2xl flex items-center justify-center mr-5 border border-purple-100">
                    <Globe className="w-7 h-7 text-purple-600" />
                </div>
                <div>
                    <h2 className="text-3xl font-bold text-gray-900">Manufacturing Economics</h2>
                    <p className="text-gray-500">Cost Efficiency for All Brands</p>
                </div>
            </div>

            <div className="prose prose-lg text-gray-600">
                <p>
                    We offer flexible tiers to support bootstrapping Indian startups and cost-efficiency for large global buyers.
                </p>

                <div className="mt-8 border border-gray-200 rounded-xl overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Tier</th>
                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Volume (Units)</th>
                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Benefit</th>
                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Ideal For</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            <tr>
                                <td className="px-6 py-4 whitespace-nowrap font-bold text-gray-900">Pilot</td>
                                <td className="px-6 py-4 text-gray-600">500 - 1,000</td>
                                <td className="px-6 py-4 text-gray-600">Low Capital Risk</td>
                                <td className="px-6 py-4 text-gray-600">Startups / New SKU</td>
                            </tr>
                            <tr>
                                <td className="px-6 py-4 whitespace-nowrap font-bold text-gray-900">Growth</td>
                                <td className="px-6 py-4 text-gray-600">1,000 - 5,000</td>
                                <td className="px-6 py-4 text-green-600 font-bold">Better Margins</td>
                                <td className="px-6 py-4 text-gray-600">Scaling Indian Brands</td>
                            </tr>
                            <tr>
                                <td className="px-6 py-4 whitespace-nowrap font-bold text-gray-900">Enterprise</td>
                                <td className="px-6 py-4 text-gray-600">10,000+</td>
                                <td className="px-6 py-4 text-green-600 font-bold">Lowest Unit Cost</td>
                                <td className="px-6 py-4 text-gray-600">Global Exports / Retail</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <h3 className="text-gray-900 font-bold mt-8 mb-4">Cost Structure Breakdown</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                    <div className="p-4 bg-gray-50 rounded-lg">
                        <div className="font-bold text-xl text-gray-900">Raw Material</div>
                        <div className="text-xs text-gray-500">Active Ingredients</div>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                        <div className="font-bold text-xl text-gray-900">Packaging</div>
                        <div className="text-xs text-gray-500">Labels & Containers</div>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                        <div className="font-bold text-xl text-gray-900">Mfg Fee</div>
                        <div className="text-xs text-gray-500">Blending & Filling</div>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                        <div className="font-bold text-xl text-gray-900">GST / Tax</div>
                        <div className="text-xs text-gray-500">Domestic vs Export</div>
                    </div>
                </div>
            </div>
        </section>

         {/* Formulation & R&D Section */}
        <section id="formulation" className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-gray-100 scroll-mt-28">
            <div className="flex items-center mb-8">
                <div className="w-14 h-14 bg-pink-50 rounded-2xl flex items-center justify-center mr-5 border border-pink-100">
                    <Beaker className="w-7 h-7 text-pink-600" />
                </div>
                <div>
                    <h2 className="text-3xl font-bold text-gray-900">Formulation & R&D</h2>
                    <p className="text-gray-500">Custom Formulas for Every Palate</p>
                </div>
            </div>

            <div className="prose prose-lg text-gray-600">
                <p>
                    Taste matters. A mango flavor that sells in Delhi might not work in London. Our R&D team specializes in regional flavor profiling for both Indian and International markets.
                </p>

                <div className="grid md:grid-cols-2 gap-8 mt-6">
                    <div>
                        <h4 className="font-bold text-gray-900 mb-3 flex items-center"><Tag className="w-4 h-4 mr-2 text-pink-500"/> Ingredient Sourcing</h4>
                        <p className="text-sm">
                            We source premium ingredients globally (Whey from USA/Europe, Herbs from India). We offer both <strong>Organic</strong> and <strong>Standard</strong> options.
                        </p>
                    </div>
                    <div>
                        <h4 className="font-bold text-gray-900 mb-3 flex items-center"><Tag className="w-4 h-4 mr-2 text-pink-500"/> Clean Label Trends</h4>
                        <p className="text-sm">
                            We specialize in "Clean Label" manufacturing—no artificial colors, no banned substances, and transparent ingredient lists that pass stringent label laws.
                        </p>
                    </div>
                </div>
            </div>
        </section>

        {/* Glossary */}
        <section id="glossary" className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-gray-100 scroll-mt-28">
            <div className="flex items-center mb-8">
                <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mr-5 border border-gray-200">
                    <BookOpen className="w-7 h-7 text-gray-700" />
                </div>
                <div>
                    <h2 className="text-3xl font-bold text-gray-900">Industry Glossary</h2>
                    <p className="text-gray-500">Terms you should know</p>
                </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
                <div>
                    <h4 className="font-bold text-gray-900">MOQ (Minimum Order Quantity)</h4>
                    <p className="text-sm text-gray-600 mt-1">The smallest number of units you must buy. We offer low MOQs to help you start.</p>
                </div>
                <div>
                    <h4 className="font-bold text-gray-900">FSSAI License</h4>
                    <p className="text-sm text-gray-600 mt-1">Mandatory license to sell food/supplements in India. We help you navigate this.</p>
                </div>
                <div>
                    <h4 className="font-bold text-gray-900">COA (Certificate of Analysis)</h4>
                    <p className="text-sm text-gray-600 mt-1">A lab report verifying that the product meets specifications (protein %, purity). Essential for trust.</p>
                </div>
                 <div>
                    <h4 className="font-bold text-gray-900">FOB / CIF (Exports)</h4>
                    <p className="text-sm text-gray-600 mt-1">Shipping terms for international clients. We handle logistics up to the port or your door.</p>
                </div>
            </div>
        </section>

      </div>

      <section className="max-w-4xl mx-auto px-4 mt-24 text-center">
         <h2 className="text-2xl font-bold text-gray-900 mb-6">Request a Quote</h2>
         <a href="/contact" className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-bold rounded-xl text-white bg-black hover:bg-orange-500 hover:text-black transition-all shadow-lg">
            Talk to an Expert <ArrowRight className="ml-2 w-5 h-5" />
         </a>
      </section>
    </div>
  );
};

export default ResourcesPage;